/**
 * Labstep
 */

import { ICONS } from 'labstep-web/constants/icons';
import { EntityView } from 'labstep-web/models/entity-view.model';
import { ExperimentWorkflowLink } from 'labstep-web/models/experiment-workflow-link.model';
import { JupyterNotebook } from 'labstep-web/models/jupyter-notebook.model';
import { Metadata } from 'labstep-web/models/metadata';
import { Molecule } from 'labstep-web/models/molecule.model';
import { ProtocolStep } from 'labstep-web/models/protocol-step.model';
import { ProtocolTable } from 'labstep-web/models/protocol-table.model';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import { MenuCommandsSection } from 'labstep-web/prosemirror/components/Menu/Commands';
import { NodeStep as NodeCode } from 'labstep-web/prosemirror/nodes/code';
import { NodeConditions } from 'labstep-web/prosemirror/nodes/conditions';
import { createConditionsNodeAndSubmitElement } from 'labstep-web/prosemirror/nodes/conditions/commands';
import { NodeDate } from 'labstep-web/prosemirror/nodes/date';
import { insertDate } from 'labstep-web/prosemirror/nodes/date/commands';
import { NodeExperiment } from 'labstep-web/prosemirror/nodes/experiment';
import { NodeExperimentWorkflowLink } from 'labstep-web/prosemirror/nodes/experiment-workflow-link';
import { NodeFile } from 'labstep-web/prosemirror/nodes/file';
import { insertHeading } from 'labstep-web/prosemirror/nodes/heading/commands';
import { NodeJupyterNotebook } from 'labstep-web/prosemirror/nodes/jupyter-notebook';
import { insertList } from 'labstep-web/prosemirror/nodes/list/commands';
import { NodeMolecule } from 'labstep-web/prosemirror/nodes/molecule';
import { NodeStep as NodeProtocolStep } from 'labstep-web/prosemirror/nodes/protocol-step';
import { NodeProtocolTable } from 'labstep-web/prosemirror/nodes/protocol-table';
import { NodeTimer as NodeProtocolTimer } from 'labstep-web/prosemirror/nodes/protocol-timer';
import { NodeValue as NodeProtocolValue } from 'labstep-web/prosemirror/nodes/protocol-value';
import { NodeSmartTable } from 'labstep-web/prosemirror/nodes/smart-table';
import type { IMenuCommandsElementProps } from './types';

export const getHeader = (
  element: IMenuCommandsElementProps,
): string => element.name;

export const MENU_COMMANDS_ELEMENTS: IMenuCommandsElementProps[] = [
  {
    id: 'heading',
    icon: 'header',
    description: 'Insert a heading (H1-H6)',
    key: 'heading',
    name: 'Heading',
    keywords: ['title', 'section'],
    submenu: [
      'heading_1',
      'heading_2',
      'heading_3',
      'heading_4',
      'heading_5',
      'heading_6',
    ],
    section: MenuCommandsSection.Common,
  },
  {
    id: 'bullet_list',
    icon: 'unordered list',
    description: 'Insert a bullet list',
    key: 'bullet_list',
    name: 'Bullet List',
    keywords: ['list', 'ul', 'bullets'],
    onCreate: insertList('bullet_list'),
    section: MenuCommandsSection.Common,
  },
  {
    id: 'ordered_list',
    icon: 'ordered list',
    description: 'Insert a numbered list',
    key: 'ordered_list',
    name: 'Numbered List',
    keywords: ['list', 'ol', 'numbers', 'sequence'],
    onCreate: insertList('ordered_list'),
    section: MenuCommandsSection.Common,
  },
  {
    id: 'protocol_step',
    icon: ICONS.protocol.step,
    description: 'Add a step to be completed',
    key: NodeProtocolStep.key,
    name: 'Step',
    entityName: ProtocolStep.entityName,
    keywords: ['step', 'todo', 'task', 'action'],
    section: MenuCommandsSection.Common,
  },
  {
    id: 'protocol_collection',
    icon: ICONS.protocol.primary,
    description:
      'Insert a protocol detailing the methods followed / to be followed',
    key: NodeExperiment.key,
    name: 'Protocol',
    keywords: ['method', 'sop', 'procedure'],
    section: MenuCommandsSection.Common,
  },
  {
    id: 'file',
    icon: 'paperclip',
    description: 'Attach / embed a file',
    key: NodeFile.key,
    name: 'File',
    keywords: ['attachment', 'document', 'upload'],
    section: MenuCommandsSection.Common,
  },
  {
    id: 'image',
    icon: 'images',
    description: 'Insert an inline image',
    key: NodeFile.key,
    name: 'Image',
    keywords: ['picture', 'photo', 'figure'],
    section: MenuCommandsSection.Common,
  },
  {
    id: 'video',
    icon: 'video',
    description: 'Embed an inline video',
    key: NodeFile.key,
    name: 'Video',
    keywords: ['video', 'movie', 'clip', 'media'],
    section: MenuCommandsSection.Common,
  },
  {
    id: 'link',
    icon: 'linkify',
    description: 'Insert a link to an external webpage',
    key: 'link',
    name: 'Link',
    keywords: ['url', 'hyperlink'],
    section: MenuCommandsSection.Common,
  },
  {
    id: 'experiment_workflow_link',
    icon: ICONS.experiment_workflow.primary,
    description: 'Link to an existing experiment',
    key: NodeExperimentWorkflowLink.key,
    entityName: ExperimentWorkflowLink.entityName,
    name: 'Linked Experiment',
    keywords: [
      'experiment',
      'link experiment',
      'reference experiment',
    ],
    section: MenuCommandsSection.Common,
  },
  {
    id: 'html_table',
    icon: 'table',
    description: 'Insert a simple table',
    key: 'table',
    name: 'Basic Table',
    keywords: ['table', 'grid'],
    section: MenuCommandsSection.Tables,
  },
  {
    id: 'protocol_timer',
    icon: ICONS.protocol.timer,
    description: 'Insert an interactive timer',
    key: NodeProtocolTimer.key,
    name: 'Timer',
    keywords: ['timer', 'countdown'],
    section: MenuCommandsSection.Common,
  },
  {
    id: 'protocol_table',
    icon: 'calculator',
    description: 'Insert a spreadsheet for doing calculations',
    key: NodeProtocolTable.key,
    entityName: ProtocolTable.entityName,
    name: 'Spreadsheet',
    keywords: [
      'spreadsheet',
      'calculator',
      'table',
      'interactive table',
    ],
    section: MenuCommandsSection.Tables,
  },
  {
    id: 'metadata_input',
    icon: ICONS.metadata.primary,
    description:
      'Insert a field for variable input parameters or files',
    key: 'metadata',
    entityName: Metadata.entityName,
    createBody: {
      type: 'file',
      is_output: false,
      is_input: true,
    },
    name: 'Input Data',
    keywords: ['input', 'file', 'parameter', 'data'],
    section: MenuCommandsSection.InputFields,
  },
  {
    id: 'metadata_output',
    icon: ICONS.metadata.primary,
    description:
      'Insert a field for capturing results and data files',
    key: 'metadata',
    entityName: Metadata.entityName,
    createBody: {
      type: 'file',
      is_output: true,
      is_input: false,
    },
    name: 'Output Data',
    keywords: ['output', 'file', 'data'],
    section: MenuCommandsSection.OutputFields,
  },
  {
    id: 'metadata_text_input',
    icon: ICONS.metadata.type.default,
    description: 'Insert a field for text-based input parameter',
    key: 'metadata',
    entityName: Metadata.entityName,
    createBody: {
      type: 'default',
      is_output: false,
      is_input: true,
    },
    name: 'Text Input',
    keywords: ['input', 'text', 'parameter', 'data'],
    section: MenuCommandsSection.InputFields,
  },
  {
    id: 'metadata_text_output',
    icon: ICONS.metadata.type.default,
    description:
      'Insert a text field for capturing qualitative results',
    key: 'metadata',
    entityName: Metadata.entityName,
    createBody: {
      type: 'default',
      is_output: true,
      is_input: false,
    },
    name: 'Text Output',
    keywords: ['output', 'result', 'text', 'data'],
    section: MenuCommandsSection.OutputFields,
  },
  {
    id: 'metadata_numeric_input',
    icon: ICONS.metadata.type.numeric,
    description:
      'Insert a field for capturing a numeric input parameter',
    key: 'metadata',
    entityName: Metadata.entityName,
    createBody: {
      type: 'numeric',
      is_output: false,
      is_input: true,
    },
    name: 'Numeric Input',
    keywords: ['number', 'input', 'parameter', 'value', 'data'],
    section: MenuCommandsSection.InputFields,
  },
  {
    id: 'metadata_numeric_output',
    icon: ICONS.metadata.type.numeric,
    description: 'Insert a field for capturing a numeric result',
    key: 'metadata',
    entityName: Metadata.entityName,
    createBody: {
      type: 'numeric',
      is_output: true,
      is_input: false,
    },
    name: 'Numeric Output',
    keywords: ['number', 'output', 'result', 'value', 'data'],
    section: MenuCommandsSection.OutputFields,
  },
  {
    id: 'metadata_date_input',
    icon: ICONS.metadata.type.date,
    description:
      'Insert a field for capturing the date an action was performed',
    key: 'metadata',
    entityName: Metadata.entityName,
    createBody: {
      type: 'date',
      is_output: false,
      is_input: true,
    },
    name: 'Date Input',
    keywords: ['date', 'input', 'day', 'data'],
    section: MenuCommandsSection.InputFields,
  },
  {
    id: 'metadata_date_output',
    icon: ICONS.metadata.type.date,
    description:
      'Insert a field for capturing the date something occurred',
    key: 'metadata',
    entityName: Metadata.entityName,
    createBody: {
      type: 'date',
      is_output: true,
      is_input: false,
    },
    name: 'Date Output',
    keywords: ['date', 'output', 'day', 'result', 'data'],
    section: MenuCommandsSection.OutputFields,
  },
  {
    id: 'metadata_options_input',
    icon: ICONS.metadata.type.options,
    description:
      'Insert a field for multiple-choice input parameters',
    key: 'metadata',
    entityName: Metadata.entityName,
    createBody: {
      type: 'options',
      is_output: false,
      is_input: true,
    },
    name: 'Options Input',
    keywords: ['dropdown', 'select', 'choice', 'input', 'data'],
    section: MenuCommandsSection.InputFields,
  },
  {
    id: 'metadata_options_output',
    icon: ICONS.metadata.type.options,
    description: 'Insert a multiple choice field for results',
    key: 'metadata',
    entityName: Metadata.entityName,
    createBody: {
      type: 'options',
      is_output: true,
      is_input: false,
    },
    name: 'Options Output',
    keywords: [
      'dropdown',
      'multiple choice',
      'select',
      'choice',
      'output',
      'result',
      'data',
    ],
    section: MenuCommandsSection.OutputFields,
  },
  {
    id: 'metadata_file_input',
    icon: ICONS.metadata.type.file,
    description: 'Insert a field for uploading input files',
    key: 'metadata',
    entityName: Metadata.entityName,
    createBody: {
      type: 'file',
      is_output: false,
      is_input: true,
    },
    name: 'Input Data File',
    keywords: ['file', 'upload', 'input', 'data'],
    section: MenuCommandsSection.InputFields,
  },
  {
    id: 'metadata_file_output',
    icon: ICONS.metadata.type.file,
    description: 'Insert a field for capturing data files produced',
    key: 'metadata',
    entityName: Metadata.entityName,
    createBody: {
      type: 'file',
      is_output: true,
      is_input: false,
    },
    name: 'Output Data File',
    keywords: ['file', 'output', 'result', 'data'],
    section: MenuCommandsSection.OutputFields,
  },
  {
    id: 'protocol_value_input',
    icon: ICONS.protocol.value,
    description:
      'Insert a field for capturing resources used / consumed',
    key: NodeProtocolValue.key,
    entityName: ProtocolValue.entityName,
    createBody: {
      is_output: false,
      is_input: true,
    },
    name: 'Inventory Used',
    premiumFeature: 'inventory',
    keywords: [
      'inventory',
      'resource',
      'consumed',
      'input',
      'reagent',
      'material',
    ],
    section: MenuCommandsSection.InputFields,
  },
  {
    id: 'protocol_value_output',
    icon: ICONS.protocol.value,
    description:
      'Insert a field for capturing resources produced / created',
    key: NodeProtocolValue.key,
    entityName: ProtocolValue.entityName,
    createBody: {
      is_output: true,
      is_input: false,
    },
    name: 'Inventory Created',
    premiumFeature: 'inventory',
    keywords: [
      'inventory',
      'resource',
      'produced',
      'output',
      'product',
      'material',
    ],
    section: MenuCommandsSection.OutputFields,
  },
  {
    id: 'molecule',
    icon: ICONS.molecule.primary,
    description:
      'Insert chemical reaction schema / molecular structures',
    key: NodeMolecule.key,
    entityName: Molecule.entityName,
    name: 'Chemistry',
    premiumFeature: 'chemistry',
    keywords: ['chemistry', 'structure', 'reaction', 'smiles'],
    section: MenuCommandsSection.DomainTools,
  },
  {
    id: 'code',
    icon: 'code',
    description: 'Display formatted code',
    key: NodeCode.key,
    name: 'Code Block',
    keywords: ['code', 'snippet', 'program'],
    section: MenuCommandsSection.Code,
  },
  {
    id: 'jupyter_notebook',
    image: JupyterNotebook.icon,
    description: 'Insert executable code for analysing data',
    key: NodeJupyterNotebook.key,
    entityName: JupyterNotebook.entityName,
    name: 'Jupyter Notebooks',
    premiumFeature: 'jupyter',
    keywords: ['jupyter', 'notebook', 'python', 'analysis', 'code'],
    section: MenuCommandsSection.Code,
  },
  {
    id: 'protocol_device',
    icon: ICONS.device.primary,
    description: 'Link a device to this experiment',
    key: 'protocol_device',
    name: 'Device Used',
    premiumFeature: 'devices',
    keywords: ['device', 'equipment', 'instrument'],
    section: MenuCommandsSection.InputFields,
  },
  {
    id: 'conditions',
    icon: ICONS.protocol_condition.primary,
    description:
      'Insert a table for specifiying inputs and outputs for different conditions',
    key: NodeConditions.key,
    name: 'Conditions Table',
    onCreate: createConditionsNodeAndSubmitElement,
    premiumFeature: 'multiplexing',
    keywords: ['conditions'],
    section: MenuCommandsSection.Tables,
  },
  {
    id: 'heading_1',
    icon: 'header',
    description: 'Large heading',
    key: 'heading_1',
    name: 'Heading 1',
    onCreate: insertHeading(1),
    section: MenuCommandsSection.Common,
  },
  {
    id: 'heading_2',
    icon: 'header',
    description: 'Medium heading',
    key: 'heading_2',
    name: 'Heading 2',
    onCreate: insertHeading(2),
    section: MenuCommandsSection.Common,
  },
  {
    id: 'heading_3',
    icon: 'header',
    description: 'Small heading',
    key: 'heading_3',
    name: 'Heading 3',
    onCreate: insertHeading(3),
    section: MenuCommandsSection.Common,
  },
  {
    id: 'heading_4',
    icon: 'header',
    description: 'Extra small heading',
    key: 'heading_4',
    name: 'Heading 4',
    onCreate: insertHeading(4),
    section: MenuCommandsSection.Common,
  },
  {
    id: 'heading_5',
    icon: 'header',
    description: 'Tiny heading',
    key: 'heading_5',
    name: 'Heading 5',
    onCreate: insertHeading(5),
    section: MenuCommandsSection.Common,
  },
  {
    id: 'heading_6',
    icon: 'header',
    description: 'Smallest heading',
    key: 'heading_6',
    name: 'Heading 6',
    onCreate: insertHeading(6),
    section: MenuCommandsSection.Common,
  },
  {
    id: 'smart_table',
    icon: ICONS.protocol_condition.primary,
    description: 'Capture structured data across multiple conditions',
    key: NodeSmartTable.key,
    name: 'Smart Table',
    premiumFeature: 'multiplexing',
    section: MenuCommandsSection.Tables,
    keywords: ['conditions'],
    submenu: [
      'smart_table_protocol_value',
      'smart_table_protocol_condition',
    ],
  },
  {
    id: 'smart_table_protocol_condition',
    icon: ICONS.protocol_condition.primary,
    description: 'Capture structured data across multiple conditions',
    key: NodeSmartTable.key,
    entityName: EntityView.entityName,
    name: 'Smart Table',
    section: MenuCommandsSection.Tables,
  },
  {
    id: 'smart_table_protocol_value',
    icon: ICONS.protocol_value.primary,
    description: 'Capture structured inventory data',
    key: NodeSmartTable.key,
    entityName: EntityView.entityName,
    name: 'Inventory',
    section: MenuCommandsSection.Tables,
  },
  {
    id: 'date',
    icon: 'calendar',
    description: 'Mark the start of a new date',
    key: NodeDate.key,
    name: 'Date',
    onCreate: insertDate,
  },
];
